import { Navigate, Route, Routes, useMatch, useParams } from "react-router-dom";
import SignInNotice from "./components/SignInNotice";
import Header from "./components/Header";
import { useEffect } from "react";
import { reportFirstTouch } from "./firstTouch";
import { useDocumentLocale } from "./i18n";
import { useSession } from "./session";
import Admin from "./pages/Admin";
import AdminActivity from "./pages/AdminActivity";
import AdminCollections from "./pages/AdminCollections";
import AdminTags from "./pages/AdminTags";
import AdminTraffic from "./pages/AdminTraffic";
import AdminErrors from "./pages/AdminErrors";
import AdminUserUsage from "./pages/AdminUserUsage";
import BookPage from "./pages/BookPage";
import BookToolPage from "./pages/BookToolPage";
import Browse from "./pages/Browse";
import ChapterPage from "./pages/ChapterPage";
import CollectionPage from "./pages/CollectionPage";
import DevicePage from "./pages/DevicePage";
import Library from "./pages/Library";
import ReadPage from "./pages/ReadPage";
import ReaderBookPage from "./pages/ReaderBookPage";
import PlusPage from "./pages/PlusPage";
import QuotesPage from "./pages/QuotesPage";
import WriterPage from "./pages/WriterPage";
import WriterEditPage from "./pages/WriterEditPage";
import Settings from "./pages/Settings";
import Groups from "./pages/Groups";
import Studio from "./pages/Studio";
import TranslatePage from "./pages/TranslatePage";
import Usage from "./pages/Usage";

/**
 * ChapterRedirect keeps /chapters/:id working.
 *
 * It sends everyone to the reader. A creator who wanted the tools is one click
 * away via "Open in studio"; sending a reader to the studio instead would show
 * them a Narrate button that spends somebody else's budget.
 */
function ChapterRedirect() {
  const { chapterID = "" } = useParams();
  return <Navigate to={`/read/${chapterID}`} replace />;
}

/**
 * A signed-out boot echoes the visitor's first touch to the beacon once
 * (ADR-0190 §2), after the session has said there is none: a signed-in reader
 * is not a visitor.
 */
function useFirstTouchBeacon() {
  const { isPending, isSignedIn } = useSession();
  useEffect(() => {
    if (!isPending && !isSignedIn) void reportFirstTouch();
  }, [isPending, isSignedIn]);
}

export default function App() {
  useFirstTouchBeacon();
  // The reader has no app header: its menu is a floating button and a drawer,
  // so the chapter gets the whole screen (ReadPage).
  const reading = useMatch("/read/:chapterID");
  // The interface language and its direction, on <html> (ADR-0023).
  useDocumentLocale();
  return (
    <>
      {!reading && <Header />}
      {/* Above the routes, and deliberately: this says why a sign-in did not
          happen, and it has to survive the page under it changing as the
          session resolves. Mounted per page it latched the reason, cleared the
          URL, and then unmounted — so the replacement found nothing and the
          refusal was silent again. */}
      {!reading && <SignInNotice />}
      <Routes>
        {/* The reader's own shelf, and the catalogue (ADR-0060). A
            signed-out visitor at / is shown the catalogue by Library itself:
            they have no library, and the URL they were sent is still theirs. */}
        <Route path="/" element={<Library />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/collections/:collectionID" element={<CollectionPage />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/studio/translate" element={<TranslatePage />} />
        <Route path="/studio/groups" element={<Groups />} />
        <Route path="/studio/writers/:writerID" element={<WriterEditPage />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/settings" element={<Settings />} />
        {/* The reader's kept quotes, each shareable as a picture (0137, 0138). */}
        <Route path="/quotes" element={<QuotesPage />} />
        {/* Platform admins only; each page sends anyone else to the library. */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/users/:userID" element={<AdminUserUsage />} />
        <Route path="/admin/traffic" element={<AdminTraffic />} />
        <Route path="/admin/errors" element={<AdminErrors />} />
        <Route path="/admin/activity" element={<AdminActivity />} />
        <Route path="/admin/collections" element={<AdminCollections />} />
        <Route path="/admin/tags" element={<AdminTags />} />
        {/* The CLI's device grant (ADR-0142). */}
        <Route path="/device" element={<DevicePage />} />
        <Route path="/books/:bookRef" element={<ReaderBookPage />} />
        <Route path="/plus" element={<PlusPage />} />
        <Route path="/writers/:writerID" element={<WriterPage />} />
        <Route path="/studio/books/:bookID" element={<BookPage />} />
        {/* The book's tools, each on its own screen (BookToolPage). */}
        <Route path="/studio/books/:bookID/publish" element={<BookToolPage tool="publish" />} />
        <Route
          path="/studio/books/:bookID/characters"
          element={<BookToolPage tool="characters" />}
        />
        <Route path="/studio/books/:bookID/cover" element={<BookToolPage tool="cover" />} />
        <Route path="/studio/books/:bookID/conflicts" element={<BookToolPage tool="conflicts" />} />
        <Route path="/studio/books/:bookID/settings" element={<BookToolPage tool="settings" />} />
        <Route path="/studio/books/:bookID/narration" element={<BookToolPage tool="narration" />} />
        <Route path="/studio/books/:bookID/voices" element={<BookToolPage tool="voices" />} />

        {/* Two views of a chapter, split by route rather than by a toggle.
            /read is the reading experience and has nothing in it that spends
            money or changes the book; /studio/chapters is where it is made.
            A URL therefore says which one you are in, and a reader's link
            shared with someone else stays a reader's link. */}
        <Route path="/read/:chapterID" element={<ReadPage />} />
        <Route path="/studio/chapters/:chapterID" element={<ChapterPage />} />
        {/* The old path, kept so links already in the wild still land
            somewhere sensible. */}
        <Route path="/chapters/:chapterID" element={<ChapterRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
